import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import ChoicesSelect from '../ChoicesSelect';
import useClickOutside from '../../hooks/useClickOutside';

interface Category {
  id: number;
  name: string;
  slug: string;
  description?: string;
  is_active: boolean;
  parent_id: number | null;
  type: string;
  level?: number;
  created_at?: string;
  updated_at?: string;
  parent?: {
    id: number;
    name: string;
  } | null;
}

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (categoryData: Partial<Category>) => void;
  category?: Category | null;
  mode: 'create' | 'edit' | 'view';
}

const CategoryModal: React.FC<CategoryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  category,
  mode
}) => {
  // Click outside handler
  const modalRef = useClickOutside(onClose);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    parent_id: null as number | null,
    type: 'category',
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableParents, setAvailableParents] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch available parent categories
  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        setLoading(true);
        const response = await apiService.get('/admin/categories/hierarchical');
        if (response.data) {
          // Filter only parent categories (level 0) for parent selection
          const parentCategories = Array.isArray(response.data) 
            ? response.data.filter((cat: Category) => cat.level === 0 || !cat.parent_id)
            : response.data.data?.filter((cat: Category) => cat.level === 0 || !cat.parent_id) || [];
          setAvailableParents(parentCategories);
        }
      } catch (error) {
        console.error('Error fetching parent categories:', error);
        setAvailableParents([]);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchParentCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (category && (mode === 'edit' || mode === 'view')) {
      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || '',
        is_active: category.is_active ?? true,
        parent_id: category.parent_id || null,
        type: category.type || 'category',
      });
    } else if (mode === 'create') {
      setFormData({
        name: '',
        slug: '',
        description: '',
        parent_id: null,
        type: 'category',
        is_active: true,
      });
    }
    setErrors({});
  }, [category, mode, isOpen]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: generateSlug(name)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    }
    if (formData.type === 'subcategory' && !formData.parent_id) {
      newErrors.parent_id = 'Parent category is required for subcategories';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  if (!isOpen) return null;

  const isReadOnly = mode === 'view';
  const title = mode === 'create' ? 'Add Category' : mode === 'edit' ? 'Edit Category' : 'View Category';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div ref={modalRef} className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={handleNameChange}
                disabled={isReadOnly}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                  isReadOnly ? 'disabled:bg-gray-100' : ''
                }`}
                placeholder="Enter category name"
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Slug *
              </label>
              <input
                type="text"
                value={formData.slug}
                readOnly
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${isReadOnly ? 'bg-gray-100' : 'bg-gray-50 text-gray-500 cursor-not-allowed focus:ring-0'}`}
                placeholder="auto-generated-slug"
              />
              {errors.slug && (
                <p className="text-sm text-red-600">{errors.slug}</p>
              )}
            </div>

            {/* Type Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Type *
              </label>
              <ChoicesSelect
                options={[
                  { value: 'category', label: 'Main Category' },
                  { value: 'subcategory', label: 'Subcategory' }
                ]}
                value={formData.type}
                onChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                placeholder="Select Type"
                disabled={isReadOnly}
                className="w-full"
              />
            </div>

            {/* Parent Category Selection */}
            {formData.type === 'subcategory' && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Parent Category *
                </label>
                <ChoicesSelect
                  options={availableParents.map(parent => ({
                    value: parent.id.toString(),
                    label: parent.name
                  }))}
                  value={formData.parent_id?.toString() || ''}
                  onChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    parent_id: value ? parseInt(value) : null 
                  }))}
                  placeholder="Select a parent category"
                  disabled={isReadOnly || loading}
                  className="w-full"
                />
                {errors.parent_id && (
                  <p className="text-sm text-red-600">{errors.parent_id}</p>
                )}
              </div>
            )}

            {/* Description - Full width */}
            <div className="md:col-span-2 space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                disabled={isReadOnly}
                rows={4}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${
                  isReadOnly ? 'disabled:bg-gray-100' : ''
                }`}
                placeholder="Enter category description (optional)"
              />
            </div>

            {/* Status - Full width */}
            <div className="md:col-span-2 space-y-2">
              <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  disabled={isReadOnly}
                  className="h-5 w-5 rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">Active Category</span>
                <span className="text-xs text-gray-500">Category will be visible and available for use</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </button>
            {!isReadOnly && (
              <button
                type="submit"
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                {mode === 'create' ? 'Create Category' : 'Update Category'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryModal;
